import { createContext, useContext } from "react";
import { extraEn, extraEs } from "./locales";

export type Locale = "en" | "es";

const en = {
  // nav
  "nav.today": "Today",
  "nav.pipelines": "Pipelines",
  "nav.customers": "Customers",
  "nav.renewals": "Renewals",
  "nav.documents": "Documents",
  "nav.settings": "Settings",
  "nav.quotes": "Quotes",
  "nav.forms": "Forms",
  "nav.carriers": "Carriers",
  "nav.import": "Import",
  "nav.guide": "Guided demo",
  "nav.signOut": "Sign out",
  "today.guide.title": "New here? Take the guided demo",
  "today.guide.body": "Eleven short steps: set up the agency, import a file, prepare a form with Faraday, exchange a message, run a carrier response, and see it all persist.",
  "today.guide.setup": "Agency setup",
  "today.guide.open": "Guided demo",
  // header
  "header.search": "Search customers, phone, policy…",
  "header.new": "New",
  "header.faraday": "Faraday",
  "header.language": "Language",
  // common
  "common.loading": "Loading…",
  "common.error": "Something went wrong.",
  "common.retry": "Try again",
  "common.empty": "Nothing here yet.",
  "common.undo": "Undo",
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.done": "Done",
  "common.open": "Open",
  "common.owner": "Owner",
  "common.due": "Due",
  "common.today": "today",
  "common.carrier": "Carrier",
  "common.premium": "Premium",
  "common.status": "Status",
  "common.language": "Language",
  "common.phone": "Phone",
  "common.city": "City",
  "common.policies": "Policies",
  "common.nextRenewal": "Next renewal",
  "common.name": "Name",
  "common.actions": "Actions",
  "common.view.list": "List",
  "common.view.board": "Board",
  "common.yes": "Yes",
  "common.no": "No",
  "common.spanish": "Spanish",
  "common.english": "English",
  "common.line": "Line",
  "common.expires": "Expires",
  "common.from": "from",
  // mode badges
  "mode.fixture": "Sample",
  "mode.sandbox": "Test",
  "mode.live": "Live",
  "mode.disabled": "Off",
  "mode.fixture.long": "Sample data, not a real result",
  "mode.sandbox.long": "Provider test environment",
  "mode.live.long": "Live provider result",
  "mode.disabled.long": "Connector is turned off",
  // today
  "today.title": "Today",
  "today.greeting": "Here is your day,",
  "today.progress": "of today's work done",
  "today.streak": "day streak",
  "today.streakGoal": "Touch every renewal due this week",
  "today.freeze": "Weekend freeze ready",
  "today.next": "Do the next thing",
  "today.tasks": "Tasks due",
  "today.renewals": "Renewals coming up",
  "today.quotes": "Quotes ready",
  "today.exceptions": "Needs a look",
  "today.allDone": "All caught up. Nice work.",
  "today.unanswered": "unanswered messages",
  "today.carriers": "carriers back",
  "today.pending": "pending",
  "today.best": "Best",
  // tasks
  "task.complete": "Mark done",
  "task.completed": "Task done",
  "task.undone": "Task reopened",
  "task.add": "Add task",
  "task.title": "What needs to happen?",
  "task.due": "Due date",
  "task.created": "Task created",
  "task.kind.follow_up": "Follow up",
  "task.kind.renewal": "Renewal",
  "task.kind.missing_info": "Missing info",
  "task.kind.service": "Service",
  "task.kind.quote_ready": "Quote ready",
  "task.kind.other": "Other",
  // renewals
  "renewals.title": "Renewals",
  "renewals.subtitle": "Every policy expiring in the next 90 days, with what changed.",
  "renewals.delta": "Change",
  "renewals.remarket": "Remarket",
  "renewals.followUp": "Follow up",
  "renewals.daysLeft": "Days left",
  "renewals.touched": "Renewal touched",
  "renewals.niceTouch": "Nice — renewal touched",
  "renewals.none": "No renewals in this window.",
  "renewals.remarketOn": "Marked for remarketing",
  "renewals.remarketOff": "Remarketing cleared",
  // pipelines
  "pipeline.sales": "Sales",
  "pipeline.renewal": "Renewal",
  "pipeline.service": "Service",
  "pipeline.cross_sell": "Cross-sell",
  "pipeline.moved": "Card moved",
  "pipeline.moveTo": "Move to…",
  "pipeline.rotting": "No touch in a while",
  "pipeline.empty": "No cards in this stage",
  "pipeline.daysSince": "days since last touch",
  // customers
  "customers.title": "Customers",
  "customers.new": "New customer",
  "customers.search": "Search by name, phone, email or policy number",
  "customers.none": "No customers match.",
  "customers.firstName": "First name",
  "customers.lastName": "Last name",
  "customers.email": "Email",
  "customers.preferredLanguage": "Preferred language",
  "customers.source": "How did they find us?",
  "customers.lineInterest": "Interested in",
  "customers.created": "Customer created",
  "customers.status.prospect": "Prospect",
  "customers.status.active": "Active",
  "customers.status.lapsed": "Lapsed",
  "customers.status.lost": "Lost",
  // customer workspace
  "customer.nextTask": "Next task",
  "customer.noTask": "No open task",
  "customer.tabs.overview": "Overview",
  "customer.tabs.quotes": "Quotes",
  "customer.tabs.policies": "Policies",
  "customer.tabs.documents": "Documents",
  "customer.tabs.activity": "Activity",
  "customer.brief": "Account brief",
  "customer.brief.badge": "AI · internal only",
  "customer.brief.none": "No brief yet.",
  "customer.notes": "Notes",
  "customer.addNote": "Add a note",
  "customer.noteAdded": "Note added",
  "customer.notePlaceholder": "What happened?",
  "customer.startQuote": "Start quote",
  "customer.consents": "Consents on file",
  "customer.noPolicies": "No policies yet.",
  "customer.noQuotes": "No quotes yet.",
  "customer.noDocs": "No documents yet.",
  "customer.prepareSummary": "Prepare application summary",
  "customer.summaryReady": "Summary ready",
  "customer.needsApplication": "Start a quote first to create an application.",
  "customer.contact": "Contact",
  "customer.address": "Address",
  "customer.tags": "Tags",
  "customer.openQuote": "Open quote",
  "customer.serviceRequests": "Service requests",
  // policy actions
  "policy.renew": "Renew",
  "policy.endorse": "Endorse",
  "policy.cancel": "Cancel",
  "policy.reinstate": "Reinstate",
  "policy.idCard": "ID card",
  "policy.coi": "COI",
  "policy.cancel.title": "Cancel this policy?",
  "policy.cancel.body": "A cancellation request will be logged on the file. Coverage does not change until the carrier confirms.",
  "policy.cancel.confirm": "Cancel policy",
  "policy.cancel.keep": "Keep policy",
  "policy.reinstate.title": "Request reinstatement?",
  "policy.reinstate.body": "A reinstatement request will be logged on the file.",
  "policy.reinstate.confirm": "Request reinstatement",
  "policy.reinstate.keep": "Not now",
  "policy.actionLogged": "Logged on the file",
  "policy.requestCreated": "Request created",
  "policy.pickPolicy": "Which policy?",
  "policy.status.active": "Active",
  "policy.status.quoted": "Quoted",
  "policy.status.bound": "Bound",
  "policy.status.pending_cancel": "Pending cancel",
  "policy.status.cancelled": "Cancelled",
  "policy.status.expired": "Expired",
  "policy.status.renewed": "Renewed",
  // quotes
  "quote.title": "Quote",
  "quote.start": "Start a quote",
  "quote.effectiveDate": "Effective date",
  "quote.vehicle": "Vehicle",
  "quote.year": "Year",
  "quote.make": "Make",
  "quote.model": "Model",
  "quote.vin": "VIN",
  "quote.driverDob": "Driver date of birth",
  "quote.sr22": "Needs SR-22",
  "quote.priorCarrier": "Prior carrier",
  "quote.request": "Get quotes",
  "quote.requested": "Quotes requested",
  "quote.completeness": "Before we rate",
  "quote.complete": "Everything needed is here.",
  "quote.missing": "Still missing",
  "quote.baseline": "Coverage you asked for",
  "quote.offers": "Carrier results",
  "quote.pending": "Waiting on carrier",
  "quote.returned": "Returned",
  "quote.declined": "Declined",
  "quote.error": "Error",
  "quote.differences": "Different from what you asked",
  "quote.select": "Select",
  "quote.selected": "Selected",
  "quote.selectNote": "Selecting records a choice. It does not bind coverage.",
  "quote.openTurboRater": "Open in TurboRater",
  "quote.downloadXml": "Download ACORD XML",
  "quote.fixtureLink": "Sample link — not connected to the agency's TurboRater yet",
  "quote.term": "term",
  "quote.months": "mo",
  "quote.waiting": "Carriers are still returning…",
  "quote.allBack": "All carriers have responded.",
  "quote.version": "Application v",
  // jobs (Faraday executor)
  "job.title": "Faraday is rating this in TurboRater",
  "job.status.queued": "Queued",
  "job.status.claimed": "Picked up",
  "job.status.running": "Working",
  "job.status.needs_human": "Needs you",
  "job.status.completed": "Done",
  "job.status.failed": "Failed",
  "job.status.cancelled": "Cancelled",
  "job.waitingExecutor": "Waiting for a Faraday executor to pick this up.",
  "job.runningOn": "Running on",
  "job.attempt": "attempt",
  "job.needsYou": "Faraday needs you",
  "job.machineOnly": "This step (sign-in, code, or verification) has to be done by you. Faraday will open the TurboRater window on your computer and continue after you finish.",
  "job.openOnMyPc": "Open on my PC",
  "job.ratherOnMyPc": "I'd rather finish this on my PC",
  "job.answerPlaceholder": "Type the answer TurboRater needs…",
  "job.send": "Send answer",
  "job.resumed": "Thanks. Faraday is continuing.",
  "job.cancel": "Stop this job",
  "job.cancelled": "Job stopped.",
  // documents
  "documents.title": "Documents",
  "documents.body": "Documents live on each customer's file. Open a customer and use the Documents tab to prepare an application summary, or upload files in a later phase.",
  "documents.where": "Where documents live",
  "documents.licensed": "Licensed ACORD forms arrive in Phase 2 once the agency's ACORD license and the forms provider are connected. Until then, summaries are branded and clearly marked as not ACORD.",
  // settings
  "settings.title": "Settings",
  "settings.connections": "Connections",
  "settings.connections.sub": "What each connector is really doing right now. No hidden fallbacks.",
  "settings.lastSuccess": "Last success",
  "settings.never": "never",
  "settings.users": "Who am I (dev)",
  "settings.users.sub": "Switch the signed-in user for the demo.",
  "settings.import": "Import",
  "settings.import.sub": "Paste or upload a CSV from Agency Matrix. Dry run first.",
  "settings.import.dryRun": "Dry run (no changes)",
  "settings.import.run": "Run import",
  "settings.import.rows": "Rows read",
  "settings.import.created": "Created",
  "settings.import.updated": "Updated",
  "settings.import.skipped": "Skipped duplicates",
  "settings.import.exceptions": "Exceptions",
  "settings.import.dob": "Date sanity",
  "settings.import.file": "Choose CSV file",
  "settings.demo": "Demo",
  "settings.demo.reset": "Reset demo data",
  "settings.demo.resetDone": "Demo data reset",
  "settings.demo.sub": "Puts the synthetic book back to its starting state. Refuses if any connector is live.",
  "settings.offices": "Offices",
  "settings.carriers": "Carriers",
  "settings.download.ivans": "IVANS download",
  "settings.download.teamup": "TEAM-UP download",
  "settings.download.portal_csv": "Portal / CSV",
  "settings.download.none": "No download",
  // faraday
  "faraday.title": "Faraday",
  "faraday.placeholder": "Try: find Maria, follow up with Jose tomorrow, renewals",
  "faraday.disclaimer": "Internal assistant. A licensed agent reviews anything sent to a customer.",
  "faraday.help": "I can: find <name> · follow up with <name> tomorrow (or in N days) · quote <name> · renewals",
  "faraday.noMatch": "I could not find anyone named",
  "faraday.found": "Found",
  "faraday.taskMade": "Follow-up created",
  "faraday.renewalsSummary": "renewals in the next 90 days",
  "faraday.opening": "Opening the quote flow for",
  "faraday.send": "Send",
  "faraday.receipt": "Receipt",
  "faraday.multiple": "More than one match — which one?",
  // misc
  "lang.en": "EN",
  "lang.es": "ES",
  "role.owner": "Owner",
  "role.agent": "Agent",
  "role.csr": "CSR",
  "role.readonly": "Read only",
  "role.techadmin": "Tech admin",
  // nav additions
  "nav.inbox": "Inbox",
  "nav.policies": "Policies",
  "nav.reports": "Reports",
  "nav.commissions": "Commissions",
  "nav.more": "More",
  // common additions
  "common.all": "All",
  "common.search": "Search",
  "common.export": "Export CSV",
  "common.exported": "Export ready",
  "common.date": "Date",
  "common.kind": "Type",
  "common.size": "Size",
  "common.send": "Send",
  "common.file": "File",
  "common.none": "None",
  "common.customer": "Customer",
  "common.title": "Title",
  "common.copy": "Copy",
  "common.copied": "Copied",
  // inbox
  "inbox.title": "Inbox",
  "inbox.subtitle": "Texts, emails and calls, all on the customer's file.",
  "inbox.none": "No conversations yet.",
  "inbox.selectThread": "Pick a conversation on the left.",
  "inbox.unread": "unread",
  "inbox.compose": "Write a message",
  "inbox.placeholder": "Type your message…",
  "inbox.subject": "Subject",
  "inbox.channel.sms": "Text",
  "inbox.channel.email": "Email",
  "inbox.channel.call": "Call",
  "inbox.channel.note": "Note",
  "inbox.category.transactional": "Service",
  "inbox.category.marketing": "Marketing",
  "inbox.marketingBlocked": "Needs marketing consent on file",
  "inbox.smsBlocked": "Needs SMS consent on file",
  "inbox.noPhone": "No phone number on file",
  "inbox.noEmail": "No email on file",
  "inbox.send": "Send",
  "inbox.sent": "Message sent",
  "inbox.blocked": "Message not sent",
  "inbox.logCall": "Log a call",
  "inbox.callSummary": "What was the call about?",
  "inbox.logged": "Call logged",
  "inbox.template": "Template",
  "inbox.noTemplate": "No template",
  "inbox.quietHours": "Outside texting hours",
  "inbox.windowOpen": "Texting hours open",
  "inbox.aiDrafted": "AI drafted",
  "inbox.modeNote": "Sample mode: nothing actually reaches a phone or an inbox.",
  "inbox.direction.inbound": "In",
  "inbox.direction.outbound": "Out",
  "inbox.status.queued": "Queued",
  "inbox.status.sent": "Sent",
  "inbox.status.delivered": "Delivered",
  "inbox.status.failed": "Failed",
  "inbox.status.received": "Received",
  "inbox.status.blocked_quiet_hours": "Blocked: quiet hours",
  "inbox.status.blocked_no_consent": "Blocked: no consent",
  "inbox.status.logged": "Logged",
  "inbox.consent.smsTransactional": "SMS service",
  "inbox.consent.smsMarketing": "SMS marketing",
  "inbox.consent.emailMarketing": "Email marketing",
  "inbox.markRead": "Mark as read",
  // consents
  "consents.title": "Consents",
  "consents.none": "No consents on file.",
  "consents.kind": "Type",
  "consents.source": "Source",
  "consents.form": "Form version",
  "consents.captured": "Captured",
  "consents.revoked": "Revoked",
  "consents.active": "Active",
  "consents.capture": "Record consent",
  "consents.capturedToast": "Consent recorded",
  "consents.revoke": "Revoke",
  "consents.revoke.title": "Revoke this consent?",
  "consents.revoke.body": "Any send that depends on it stops immediately. This is logged on the file.",
  "consents.revoke.confirm": "Revoke consent",
  "consents.revoke.keep": "Keep consent",
  "consents.revokedToast": "Consent revoked",
  "consents.text": "Exact text shown to the customer",
  "consents.channelValue": "Phone or email it applies to",
  "consents.kind.sms_transactional": "SMS service messages",
  "consents.kind.sms_marketing": "SMS marketing",
  "consents.kind.email_marketing": "Email marketing",
  "consents.kind.e_delivery": "Electronic delivery",
  "consents.kind.credit_disclosure": "Credit disclosure",
  "consents.kind.privacy_notice": "Privacy notice",
  "consents.source.in_person": "In person",
  "consents.source.phone": "Phone",
  "consents.source.web_form": "Web form",
  "consents.source.sms_reply": "Text reply",
  "consents.source.in_app": "In app",
  "consents.text.sms_transactional": "You agree to receive text messages from GO Insurance about your quote and policies. Message and data rates may apply. Reply STOP to opt out, HELP for help.",
  "consents.text.sms_marketing": "You agree to receive marketing text messages from GO Insurance, including offers and reminders. Consent is not a condition of purchase. Message and data rates may apply. Reply STOP to opt out, HELP for help.",
  "consents.text.email_marketing": "You agree to receive marketing emails from GO Insurance. You can unsubscribe at any time using the link in every email.",
  "consents.text.e_delivery": "You agree to receive policy documents and notices electronically. You may withdraw this consent and request paper copies at any time. Cancellation notices are also sent by mail.",
  "consents.text.credit_disclosure": "In connection with this application, we may obtain and use credit information and other consumer reports to rate your policy, as permitted by Texas law.",
  "consents.text.privacy_notice": "You received GO Insurance's privacy notice explaining how we collect, use and protect your information.",
  // documents
  "documents.subtitle": "Every file on every customer, with its checksum and where it came from.",
  "documents.upload": "Upload a file",
  "documents.pick": "Choose file",
  "documents.uploading": "Uploading…",
  "documents.uploaded": "File uploaded",
  "documents.tooLarge": "That file is larger than 10 MB.",
  "documents.titleField": "Title",
  "documents.kind.upload": "Upload",
  "documents.kind.dec_page": "Dec page",
  "documents.kind.id_card": "ID card",
  "documents.kind.certificate": "Certificate",
  "documents.kind.application_summary": "Application summary",
  "documents.kind.acord_form": "ACORD form",
  "documents.kind.export": "Export",
  "documents.none": "No documents yet.",
  "documents.checksum": "Checksum",
  // bridge
  "bridge.import": "Import TurboRater bridge file",
  "bridge.help": "In TurboRater, use Bridge to export the quote, then pick that file here (ACORD XML or TT2).",
  "bridge.pick": "Choose bridge file",
  "bridge.imported": "Bridge file imported",
  "bridge.format": "Format",
  "bridge.warnings": "Warnings",
  "bridge.noQuote": "Imported as application version",
  "bridge.openQuote": "Open the imported quote",
  // policies
  "policies.title": "Policies",
  "policies.subtitle": "The whole book, with where each policy came from.",
  "policies.search": "Search name or policy number",
  "policies.none": "No policies match.",
  "policies.effective": "Effective",
  "policies.source": "Source",
  "policies.source.import_csv": "CSV import",
  "policies.source.download_al3": "Carrier download",
  "policies.source.bridge_tt2": "TurboRater bridge",
  "policies.source.manual": "Manual",
  "policies.source.api": "API",
  "policies.count": "policies",
  // reports
  "reports.title": "Reports",
  "reports.subtitle": "The numbers the owner asks for, straight from the book.",
  "reports.book_of_business": "Book of business",
  "reports.production": "Production",
  "reports.retention": "Retention",
  "reports.commissions": "Commissions",
  "reports.activity": "Activity",
  "reports.from": "From",
  "reports.to": "To",
  "reports.totals": "Totals",
  "reports.none": "No rows for this range.",
  "reports.pulse": "Agency Pulse",
  "reports.pulse.retention": "Retention",
  "reports.pulse.due30": "Renewals due in 30 days",
  "reports.pulse.avgChange": "Average premium change",
  "reports.generated": "Generated",
  // commissions
  "commissions.title": "Commissions",
  "commissions.subtitle": "Upload a carrier statement and every line gets matched to a policy.",
  "commissions.carrier": "Carrier",
  "commissions.statementDate": "Statement date",
  "commissions.file": "Choose statement CSV",
  "commissions.paste": "Or paste the CSV",
  "commissions.dryRun": "Dry run (no changes)",
  "commissions.import": "Import statement",
  "commissions.imported": "Statement imported",
  "commissions.matched": "Matched",
  "commissions.unmatched": "Unmatched",
  "commissions.duplicates": "Duplicates",
  "commissions.total": "Total commission",
  "commissions.lines": "Lines",
  "commissions.history": "Statements",
  "commissions.none": "No statements yet.",
  "commissions.policyNumber": "Policy #",
  "commissions.insured": "Insured",
  "commissions.rate": "Rate",
  "commissions.commission": "Commission",
  "commissions.warnings": "Warnings",
  "commissions.kind.new_business": "New business",
  "commissions.kind.renewal": "Renewal",
  "commissions.kind.endorsement": "Endorsement",
  "commissions.kind.cancellation": "Cancellation",
  "commissions.kind.adjustment": "Adjustment",
  "commissions.kind.unknown": "Unknown",
  "commissions.status.matched": "Matched",
  "commissions.status.unmatched": "Unmatched",
  "commissions.status.duplicate": "Duplicate",
  // intake (public)
  "intake.title": "Get a quote",
  "intake.subtitle": "Tell us a little and a licensed agent will call you back.",
  "intake.firstName": "First name",
  "intake.lastName": "Last name",
  "intake.phone": "Phone",
  "intake.email": "Email",
  "intake.line": "What do you need?",
  "intake.notes": "Anything we should know?",
  "intake.language": "Preferred language",
  "intake.consent.sms": "By checking this box you agree to receive text messages from GO Insurance about your quote and policies. Message and data rates may apply. Reply STOP to opt out, HELP for help.",
  "intake.submit": "Send my request",
  "intake.sending": "Sending…",
  "intake.thanks": "Got it. We will call you soon.",
  "intake.thanksBody": "Prefer to talk now? Call us.",
  "intake.required": "Please give us your name and a phone number or email.",
  "intake.privacy": "We only use this to prepare your quote.",
  "intake.another": "Send another request",
  "intake.line.auto": "Auto",
  "intake.line.home": "Home",
  "intake.line.renters": "Renters",
  "intake.line.commercial_auto": "Commercial auto",
  "intake.line.general_liability": "General liability",
  "intake.line.bop": "Business owners",
  "intake.line.life": "Life",
  "intake.line.health": "Health",
  // settings additions
  "settings.templates": "Message templates",
  "settings.templates.sub": "What the team can send in one tap, in both languages.",
  "settings.intake": "Public quote link",
  "settings.intake.sub": "Put this link on the website and social media. Requests land in the Sales pipeline with consent recorded.",
  "settings.copyLink": "Copy link",
  "settings.copied": "Link copied",
} as const;

export type TKey = keyof typeof en;

const es: Record<string, string> = {
  "nav.today": "Hoy",
  "nav.pipelines": "Pipelines",
  "nav.customers": "Clientes",
  "nav.renewals": "Renovaciones",
  "nav.documents": "Documentos",
  "nav.settings": "Ajustes",
  "nav.quotes": "Cotizaciones",
  "nav.forms": "Formularios",
  "nav.carriers": "Aseguradoras",
  "nav.import": "Importar",
  "nav.guide": "Demo guiada",
  "nav.signOut": "Cerrar sesión",
  "today.guide.title": "¿Primera vez? Haz la demo guiada",
  "today.guide.body": "Once pasos cortos: configura la agencia, importa un archivo, prepara un formulario con Faraday, intercambia un mensaje, corre una respuesta de aseguradora y comprueba que todo persiste.",
  "today.guide.setup": "Configuración",
  "today.guide.open": "Demo guiada",
  "header.search": "Buscar cliente, teléfono, póliza…",
  "header.new": "Nuevo",
  "header.faraday": "Faraday",
  "header.language": "Idioma",
  "common.loading": "Cargando…",
  "common.error": "Algo salió mal.",
  "common.retry": "Intentar de nuevo",
  "common.empty": "Todavía no hay nada aquí.",
  "common.undo": "Deshacer",
  "common.save": "Guardar",
  "common.cancel": "Cancelar",
  "common.close": "Cerrar",
  "common.done": "Listo",
  "common.open": "Abrir",
  "common.owner": "Responsable",
  "common.due": "Vence",
  "common.today": "hoy",
  "common.carrier": "Aseguradora",
  "common.premium": "Prima",
  "common.status": "Estado",
  "common.language": "Idioma",
  "common.phone": "Teléfono",
  "common.city": "Ciudad",
  "common.policies": "Pólizas",
  "common.nextRenewal": "Próxima renovación",
  "common.name": "Nombre",
  "common.actions": "Acciones",
  "common.view.list": "Lista",
  "common.view.board": "Tablero",
  "common.yes": "Sí",
  "common.no": "No",
  "common.spanish": "Español",
  "common.english": "Inglés",
  "common.line": "Línea",
  "common.expires": "Vence",
  "common.from": "de",
  "mode.fixture": "Muestra",
  "mode.sandbox": "Prueba",
  "mode.live": "En vivo",
  "mode.disabled": "Apagado",
  "mode.fixture.long": "Datos de muestra, no es un resultado real",
  "mode.sandbox.long": "Ambiente de prueba del proveedor",
  "mode.live.long": "Resultado real del proveedor",
  "mode.disabled.long": "El conector está apagado",
  "today.title": "Hoy",
  "today.greeting": "Así va tu día,",
  "today.progress": "del trabajo de hoy terminado",
  "today.streak": "días seguidos",
  "today.streakGoal": "Atender cada renovación que vence esta semana",
  "today.freeze": "Pausa de fin de semana lista",
  "today.next": "Hacer lo que sigue",
  "today.tasks": "Tareas de hoy",
  "today.renewals": "Renovaciones próximas",
  "today.quotes": "Cotizaciones listas",
  "today.exceptions": "Necesita revisión",
  "today.allDone": "Todo al día. ¡Buen trabajo!",
  "today.unanswered": "mensajes sin responder",
  "today.carriers": "aseguradoras respondieron",
  "today.pending": "pendientes",
  "today.best": "Mejor",
  "task.complete": "Marcar como hecha",
  "task.completed": "Tarea terminada",
  "task.undone": "Tarea reabierta",
  "task.add": "Agregar tarea",
  "task.title": "¿Qué hay que hacer?",
  "task.due": "Fecha límite",
  "task.created": "Tarea creada",
  "task.kind.follow_up": "Seguimiento",
  "task.kind.renewal": "Renovación",
  "task.kind.missing_info": "Falta información",
  "task.kind.service": "Servicio",
  "task.kind.quote_ready": "Cotización lista",
  "task.kind.other": "Otro",
  "renewals.title": "Renovaciones",
  "renewals.subtitle": "Cada póliza que vence en los próximos 90 días, y qué cambió.",
  "renewals.delta": "Cambio",
  "renewals.remarket": "Recotizar",
  "renewals.followUp": "Seguimiento",
  "renewals.daysLeft": "Días restantes",
  "renewals.touched": "Renovación atendida",
  "renewals.niceTouch": "Bien — renovación atendida",
  "renewals.none": "No hay renovaciones en este periodo.",
  "renewals.remarketOn": "Marcada para recotizar",
  "renewals.remarketOff": "Recotización quitada",
  "pipeline.sales": "Ventas",
  "pipeline.renewal": "Renovación",
  "pipeline.service": "Servicio",
  "pipeline.cross_sell": "Venta cruzada",
  "pipeline.moved": "Tarjeta movida",
  "pipeline.moveTo": "Mover a…",
  "pipeline.rotting": "Sin contacto desde hace tiempo",
  "pipeline.empty": "No hay tarjetas en esta etapa",
  "pipeline.daysSince": "días sin contacto",
  "customers.title": "Clientes",
  "customers.new": "Nuevo cliente",
  "customers.search": "Buscar por nombre, teléfono, correo o número de póliza",
  "customers.none": "Ningún cliente coincide.",
  "customers.firstName": "Nombre",
  "customers.lastName": "Apellido",
  "customers.email": "Correo",
  "customers.preferredLanguage": "Idioma preferido",
  "customers.source": "¿Cómo nos encontró?",
  "customers.lineInterest": "Le interesa",
  "customers.created": "Cliente creado",
  "customers.status.prospect": "Prospecto",
  "customers.status.active": "Activo",
  "customers.status.lapsed": "Vencido",
  "customers.status.lost": "Perdido",
  "customer.nextTask": "Siguiente tarea",
  "customer.noTask": "Sin tareas abiertas",
  "customer.tabs.overview": "Resumen",
  "customer.tabs.quotes": "Cotizaciones",
  "customer.tabs.policies": "Pólizas",
  "customer.tabs.documents": "Documentos",
  "customer.tabs.activity": "Actividad",
  "customer.brief": "Resumen de la cuenta",
  "customer.brief.badge": "IA · solo uso interno",
  "customer.brief.none": "Todavía no hay resumen.",
  "customer.notes": "Notas",
  "customer.addNote": "Agregar nota",
  "customer.noteAdded": "Nota agregada",
  "customer.notePlaceholder": "¿Qué pasó?",
  "customer.startQuote": "Cotizar",
  "customer.consents": "Consentimientos registrados",
  "customer.noPolicies": "Todavía no hay pólizas.",
  "customer.noQuotes": "Todavía no hay cotizaciones.",
  "customer.noDocs": "Todavía no hay documentos.",
  "customer.prepareSummary": "Preparar resumen de solicitud",
  "customer.summaryReady": "Resumen listo",
  "customer.needsApplication": "Primero inicia una cotización para crear una solicitud.",
  "customer.contact": "Contacto",
  "customer.address": "Dirección",
  "customer.tags": "Etiquetas",
  "customer.openQuote": "Abrir cotización",
  "customer.serviceRequests": "Solicitudes de servicio",
  "policy.renew": "Renovar",
  "policy.endorse": "Endoso",
  "policy.cancel": "Cancelar",
  "policy.reinstate": "Reinstalar",
  "policy.idCard": "Tarjeta ID",
  "policy.coi": "Certificado",
  "policy.cancel.title": "¿Cancelar esta póliza?",
  "policy.cancel.body": "Se registrará una solicitud de cancelación en el expediente. La cobertura no cambia hasta que la aseguradora confirme.",
  "policy.cancel.confirm": "Cancelar póliza",
  "policy.cancel.keep": "Mantener póliza",
  "policy.reinstate.title": "¿Solicitar reinstalación?",
  "policy.reinstate.body": "Se registrará una solicitud de reinstalación en el expediente.",
  "policy.reinstate.confirm": "Solicitar reinstalación",
  "policy.reinstate.keep": "Ahora no",
  "policy.actionLogged": "Registrado en el expediente",
  "policy.requestCreated": "Solicitud creada",
  "policy.pickPolicy": "¿Cuál póliza?",
  "policy.status.active": "Activa",
  "policy.status.quoted": "Cotizada",
  "policy.status.bound": "Emitida",
  "policy.status.pending_cancel": "Cancelación pendiente",
  "policy.status.cancelled": "Cancelada",
  "policy.status.expired": "Vencida",
  "policy.status.renewed": "Renovada",
  "quote.title": "Cotización",
  "quote.start": "Iniciar cotización",
  "quote.effectiveDate": "Fecha de inicio",
  "quote.vehicle": "Vehículo",
  "quote.year": "Año",
  "quote.make": "Marca",
  "quote.model": "Modelo",
  "quote.vin": "VIN",
  "quote.driverDob": "Fecha de nacimiento del conductor",
  "quote.sr22": "Necesita SR-22",
  "quote.priorCarrier": "Aseguradora anterior",
  "quote.request": "Obtener cotizaciones",
  "quote.requested": "Cotizaciones solicitadas",
  "quote.completeness": "Antes de cotizar",
  "quote.complete": "Ya está todo lo necesario.",
  "quote.missing": "Todavía falta",
  "quote.baseline": "Cobertura que pediste",
  "quote.offers": "Resultados por aseguradora",
  "quote.pending": "Esperando a la aseguradora",
  "quote.returned": "Respondió",
  "quote.declined": "Declinó",
  "quote.error": "Error",
  "quote.differences": "Diferente a lo que pediste",
  "quote.select": "Elegir",
  "quote.selected": "Elegida",
  "quote.selectNote": "Elegir registra una preferencia. No emite ni establece cobertura.",
  "quote.openTurboRater": "Abrir en TurboRater",
  "quote.downloadXml": "Descargar ACORD XML",
  "quote.fixtureLink": "Enlace de muestra — todavía no está conectado al TurboRater de la agencia",
  "quote.term": "vigencia",
  "quote.months": "meses",
  "quote.waiting": "Las aseguradoras siguen respondiendo…",
  "quote.allBack": "Todas las aseguradoras respondieron.",
  "quote.version": "Solicitud v",
  "job.title": "Faraday está cotizando en TurboRater",
  "job.status.queued": "En cola",
  "job.status.claimed": "Tomado",
  "job.status.running": "Trabajando",
  "job.status.needs_human": "Te necesita",
  "job.status.completed": "Listo",
  "job.status.failed": "Falló",
  "job.status.cancelled": "Cancelado",
  "job.waitingExecutor": "Esperando a que un ejecutor de Faraday lo tome.",
  "job.runningOn": "Corriendo en",
  "job.attempt": "intento",
  "job.needsYou": "Faraday te necesita",
  "job.machineOnly": "Este paso (inicio de sesión, código o verificación) lo tienes que hacer tú. Faraday abrirá la ventana de TurboRater en tu computadora y continuará cuando termines.",
  "job.openOnMyPc": "Abrir en mi PC",
  "job.ratherOnMyPc": "Prefiero terminar esto en mi PC",
  "job.answerPlaceholder": "Escribe la respuesta que pide TurboRater…",
  "job.send": "Enviar respuesta",
  "job.resumed": "Gracias. Faraday continúa.",
  "job.cancel": "Detener este trabajo",
  "job.cancelled": "Trabajo detenido.",
  "documents.title": "Documentos",
  "documents.body": "Los documentos viven en el expediente de cada cliente. Abre un cliente y usa la pestaña Documentos para preparar un resumen de solicitud; subir archivos llega en una fase posterior.",
  "documents.where": "Dónde están los documentos",
  "documents.licensed": "Los formularios ACORD con licencia llegan en la Fase 2, cuando la licencia ACORD de la agencia y el proveedor de formularios estén conectados. Mientras tanto, los resúmenes llevan la marca de la agencia y dicen claramente que no son ACORD.",
  "settings.title": "Ajustes",
  "settings.connections": "Conexiones",
  "settings.connections.sub": "Lo que cada conector está haciendo realmente ahora. Sin respaldos ocultos.",
  "settings.lastSuccess": "Último éxito",
  "settings.never": "nunca",
  "settings.users": "¿Quién soy? (dev)",
  "settings.users.sub": "Cambia el usuario con sesión para la demostración.",
  "settings.import": "Importar",
  "settings.import.sub": "Pega o sube un CSV de Agency Matrix. Primero en modo de prueba.",
  "settings.import.dryRun": "Prueba (sin cambios)",
  "settings.import.run": "Ejecutar importación",
  "settings.import.rows": "Filas leídas",
  "settings.import.created": "Creados",
  "settings.import.updated": "Actualizados",
  "settings.import.skipped": "Duplicados omitidos",
  "settings.import.exceptions": "Excepciones",
  "settings.import.dob": "Revisión de fechas",
  "settings.import.file": "Elegir archivo CSV",
  "settings.demo": "Demostración",
  "settings.demo.reset": "Reiniciar datos de demo",
  "settings.demo.resetDone": "Datos de demo reiniciados",
  "settings.demo.sub": "Regresa la cartera sintética a su estado inicial. Se niega si algún conector está en vivo.",
  "settings.offices": "Oficinas",
  "settings.carriers": "Aseguradoras",
  "settings.download.ivans": "Descarga IVANS",
  "settings.download.teamup": "Descarga TEAM-UP",
  "settings.download.portal_csv": "Portal / CSV",
  "settings.download.none": "Sin descarga",
  "faraday.title": "Faraday",
  "faraday.placeholder": "Prueba: buscar María, seguimiento con José mañana, renovaciones",
  "faraday.disclaimer": "Asistente interno. Un agente con licencia revisa todo lo que se envía a un cliente.",
  "faraday.help": "Puedo: buscar <nombre> · seguimiento con <nombre> mañana (o en N días) · cotizar <nombre> · renovaciones",
  "faraday.noMatch": "No encontré a nadie llamado",
  "faraday.found": "Encontré",
  "faraday.taskMade": "Seguimiento creado",
  "faraday.renewalsSummary": "renovaciones en los próximos 90 días",
  "faraday.opening": "Abriendo la cotización de",
  "faraday.send": "Enviar",
  "faraday.receipt": "Recibo",
  "faraday.multiple": "Hay más de una coincidencia — ¿cuál?",
  "lang.en": "EN",
  "lang.es": "ES",
  "role.owner": "Dueño",
  "role.agent": "Agente",
  "role.csr": "CSR",
  "role.readonly": "Solo lectura",
  "role.techadmin": "Admin técnico",
  "nav.inbox": "Mensajes",
  "nav.policies": "Pólizas",
  "nav.reports": "Reportes",
  "nav.commissions": "Comisiones",
  "nav.more": "Más",
  "common.all": "Todos",
  "common.search": "Buscar",
  "common.export": "Exportar CSV",
  "common.exported": "Exportación lista",
  "common.date": "Fecha",
  "common.kind": "Tipo",
  "common.size": "Tamaño",
  "common.send": "Enviar",
  "common.file": "Archivo",
  "common.none": "Ninguno",
  "common.customer": "Cliente",
  "common.title": "Título",
  "common.copy": "Copiar",
  "common.copied": "Copiado",
  "inbox.title": "Mensajes",
  "inbox.subtitle": "Textos, correos y llamadas, todo en el expediente del cliente.",
  "inbox.none": "Todavía no hay conversaciones.",
  "inbox.selectThread": "Elige una conversación a la izquierda.",
  "inbox.unread": "sin leer",
  "inbox.compose": "Escribir un mensaje",
  "inbox.placeholder": "Escribe tu mensaje…",
  "inbox.subject": "Asunto",
  "inbox.channel.sms": "Texto",
  "inbox.channel.email": "Correo",
  "inbox.channel.call": "Llamada",
  "inbox.channel.note": "Nota",
  "inbox.category.transactional": "Servicio",
  "inbox.category.marketing": "Promoción",
  "inbox.marketingBlocked": "Falta el consentimiento de promociones",
  "inbox.smsBlocked": "Falta el consentimiento de SMS",
  "inbox.noPhone": "No hay teléfono en el expediente",
  "inbox.noEmail": "No hay correo en el expediente",
  "inbox.send": "Enviar",
  "inbox.sent": "Mensaje enviado",
  "inbox.blocked": "Mensaje no enviado",
  "inbox.logCall": "Registrar llamada",
  "inbox.callSummary": "¿De qué fue la llamada?",
  "inbox.logged": "Llamada registrada",
  "inbox.template": "Plantilla",
  "inbox.noTemplate": "Sin plantilla",
  "inbox.quietHours": "Fuera del horario para textos",
  "inbox.windowOpen": "Horario para textos abierto",
  "inbox.aiDrafted": "Borrador de IA",
  "inbox.modeNote": "Modo de muestra: nada llega realmente a un teléfono ni a un correo.",
  "inbox.direction.inbound": "Entrante",
  "inbox.direction.outbound": "Saliente",
  "inbox.status.queued": "En cola",
  "inbox.status.sent": "Enviado",
  "inbox.status.delivered": "Entregado",
  "inbox.status.failed": "Falló",
  "inbox.status.received": "Recibido",
  "inbox.status.blocked_quiet_hours": "Bloqueado: fuera de horario",
  "inbox.status.blocked_no_consent": "Bloqueado: sin consentimiento",
  "inbox.status.logged": "Registrado",
  "inbox.consent.smsTransactional": "SMS servicio",
  "inbox.consent.smsMarketing": "SMS promoción",
  "inbox.consent.emailMarketing": "Correo promoción",
  "inbox.markRead": "Marcar como leído",
  "consents.title": "Consentimientos",
  "consents.none": "No hay consentimientos registrados.",
  "consents.kind": "Tipo",
  "consents.source": "Origen",
  "consents.form": "Versión del texto",
  "consents.captured": "Registrado",
  "consents.revoked": "Revocado",
  "consents.active": "Vigente",
  "consents.capture": "Registrar consentimiento",
  "consents.capturedToast": "Consentimiento registrado",
  "consents.revoke": "Revocar",
  "consents.revoke.title": "¿Revocar este consentimiento?",
  "consents.revoke.body": "Cualquier envío que dependa de él se detiene de inmediato. Queda registrado en el expediente.",
  "consents.revoke.confirm": "Revocar consentimiento",
  "consents.revoke.keep": "Mantener consentimiento",
  "consents.revokedToast": "Consentimiento revocado",
  "consents.text": "Texto exacto que se le mostró al cliente",
  "consents.channelValue": "Teléfono o correo al que aplica",
  "consents.kind.sms_transactional": "Mensajes SMS de servicio",
  "consents.kind.sms_marketing": "SMS de promoción",
  "consents.kind.email_marketing": "Correo de promoción",
  "consents.kind.e_delivery": "Entrega electrónica",
  "consents.kind.credit_disclosure": "Aviso de uso de crédito",
  "consents.kind.privacy_notice": "Aviso de privacidad",
  "consents.source.in_person": "En persona",
  "consents.source.phone": "Teléfono",
  "consents.source.web_form": "Formulario web",
  "consents.source.sms_reply": "Respuesta por texto",
  "consents.source.in_app": "En la aplicación",
  "consents.text.sms_transactional": "Usted acepta recibir mensajes de texto de GO Insurance sobre su cotización y pólizas. Pueden aplicar tarifas de mensajes y datos. Responda ALTO para cancelar, AYUDA para ayuda.",
  "consents.text.sms_marketing": "Usted acepta recibir mensajes de texto promocionales de GO Insurance, incluidas ofertas y recordatorios. El consentimiento no es condición de compra. Pueden aplicar tarifas de mensajes y datos. Responda ALTO para cancelar, AYUDA para ayuda.",
  "consents.text.email_marketing": "Usted acepta recibir correos promocionales de GO Insurance. Puede cancelar en cualquier momento con el enlace que aparece en cada correo.",
  "consents.text.e_delivery": "Usted acepta recibir documentos y avisos de su póliza por medios electrónicos. Puede retirar este consentimiento y pedir copias en papel en cualquier momento. Los avisos de cancelación también se envían por correo postal.",
  "consents.text.credit_disclosure": "En relación con esta solicitud, podemos obtener y usar información de crédito y otros reportes del consumidor para tarificar su póliza, según lo permite la ley de Texas.",
  "consents.text.privacy_notice": "Usted recibió el aviso de privacidad de GO Insurance que explica cómo recopilamos, usamos y protegemos su información.",
  "documents.subtitle": "Cada archivo de cada cliente, con su verificación y de dónde viene.",
  "documents.upload": "Subir un archivo",
  "documents.pick": "Elegir archivo",
  "documents.uploading": "Subiendo…",
  "documents.uploaded": "Archivo subido",
  "documents.tooLarge": "Ese archivo pesa más de 10 MB.",
  "documents.titleField": "Título",
  "documents.kind.upload": "Archivo",
  "documents.kind.dec_page": "Carátula de póliza",
  "documents.kind.id_card": "Tarjeta ID",
  "documents.kind.certificate": "Certificado",
  "documents.kind.application_summary": "Resumen de solicitud",
  "documents.kind.acord_form": "Formulario ACORD",
  "documents.kind.export": "Exportación",
  "documents.none": "Todavía no hay documentos.",
  "documents.checksum": "Verificación",
  "bridge.import": "Importar archivo bridge de TurboRater",
  "bridge.help": "En TurboRater usa Bridge para exportar la cotización y luego elige ese archivo aquí (ACORD XML o TT2).",
  "bridge.pick": "Elegir archivo bridge",
  "bridge.imported": "Archivo bridge importado",
  "bridge.format": "Formato",
  "bridge.warnings": "Advertencias",
  "bridge.noQuote": "Importado como versión de solicitud",
  "bridge.openQuote": "Abrir la cotización importada",
  "policies.title": "Pólizas",
  "policies.subtitle": "Toda la cartera, con el origen de cada póliza.",
  "policies.search": "Buscar nombre o número de póliza",
  "policies.none": "Ninguna póliza coincide.",
  "policies.effective": "Inicio",
  "policies.source": "Origen",
  "policies.source.import_csv": "Importación CSV",
  "policies.source.download_al3": "Descarga de aseguradora",
  "policies.source.bridge_tt2": "Bridge de TurboRater",
  "policies.source.manual": "Manual",
  "policies.source.api": "API",
  "policies.count": "pólizas",
  "reports.title": "Reportes",
  "reports.subtitle": "Los números que pide el dueño, directo de la cartera.",
  "reports.book_of_business": "Cartera",
  "reports.production": "Producción",
  "reports.retention": "Retención",
  "reports.commissions": "Comisiones",
  "reports.activity": "Actividad",
  "reports.from": "Desde",
  "reports.to": "Hasta",
  "reports.totals": "Totales",
  "reports.none": "No hay filas para este periodo.",
  "reports.pulse": "Pulso de la agencia",
  "reports.pulse.retention": "Retención",
  "reports.pulse.due30": "Renovaciones en 30 días",
  "reports.pulse.avgChange": "Cambio promedio de prima",
  "reports.generated": "Generado",
  "commissions.title": "Comisiones",
  "commissions.subtitle": "Sube el estado de cuenta de la aseguradora y cada línea se empareja con una póliza.",
  "commissions.carrier": "Aseguradora",
  "commissions.statementDate": "Fecha del estado",
  "commissions.file": "Elegir CSV del estado",
  "commissions.paste": "O pega el CSV",
  "commissions.dryRun": "Prueba (sin cambios)",
  "commissions.import": "Importar estado",
  "commissions.imported": "Estado importado",
  "commissions.matched": "Emparejadas",
  "commissions.unmatched": "Sin emparejar",
  "commissions.duplicates": "Duplicadas",
  "commissions.total": "Comisión total",
  "commissions.lines": "Líneas",
  "commissions.history": "Estados de cuenta",
  "commissions.none": "Todavía no hay estados de cuenta.",
  "commissions.policyNumber": "Póliza #",
  "commissions.insured": "Asegurado",
  "commissions.rate": "Tasa",
  "commissions.commission": "Comisión",
  "commissions.warnings": "Advertencias",
  "commissions.kind.new_business": "Nuevo negocio",
  "commissions.kind.renewal": "Renovación",
  "commissions.kind.endorsement": "Endoso",
  "commissions.kind.cancellation": "Cancelación",
  "commissions.kind.adjustment": "Ajuste",
  "commissions.kind.unknown": "Desconocido",
  "commissions.status.matched": "Emparejada",
  "commissions.status.unmatched": "Sin emparejar",
  "commissions.status.duplicate": "Duplicada",
  "intake.title": "Pide tu cotización",
  "intake.subtitle": "Cuéntanos un poco y un agente con licencia te llamará.",
  "intake.firstName": "Nombre",
  "intake.lastName": "Apellido",
  "intake.phone": "Teléfono",
  "intake.email": "Correo",
  "intake.line": "¿Qué necesitas?",
  "intake.notes": "¿Algo que debamos saber?",
  "intake.language": "Idioma preferido",
  "intake.consent.sms": "Al marcar esta casilla acepta recibir mensajes de texto de GO Insurance sobre su cotización y pólizas. Pueden aplicar tarifas de mensajes y datos. Responda ALTO para cancelar, AYUDA para ayuda.",
  "intake.submit": "Enviar mi solicitud",
  "intake.sending": "Enviando…",
  "intake.thanks": "Listo. Te llamamos pronto.",
  "intake.thanksBody": "¿Prefieres hablar ahora? Llámanos.",
  "intake.required": "Danos tu nombre y un teléfono o correo.",
  "intake.privacy": "Solo usamos esto para preparar tu cotización.",
  "intake.another": "Enviar otra solicitud",
  "intake.line.auto": "Auto",
  "intake.line.home": "Casa",
  "intake.line.renters": "Inquilinos",
  "intake.line.commercial_auto": "Auto comercial",
  "intake.line.general_liability": "Responsabilidad civil",
  "intake.line.bop": "Paquete de negocio",
  "intake.line.life": "Vida",
  "intake.line.health": "Salud",
  "settings.templates": "Plantillas de mensajes",
  "settings.templates.sub": "Lo que el equipo puede enviar con un toque, en ambos idiomas.",
  "settings.intake": "Enlace público de cotización",
  "settings.intake.sub": "Ponlo en el sitio web y en redes. Las solicitudes caen en el pipeline de Ventas con el consentimiento registrado.",
  "settings.copyLink": "Copiar enlace",
  "settings.copied": "Enlace copiado",
};

const dictionaries: Record<Locale, Record<string, string>> = { en: { ...en, ...extraEn }, es: { ...es, ...extraEs } };

export function translate(locale: Locale, key: TKey | string): string {
  const d = dictionaries[locale];
  return d[key] ?? dictionaries.en[key] ?? key;
}

export const LocaleContext = createContext<{ locale: Locale; setLocale: (l: Locale) => void }>({ locale: "en", setLocale: () => {} });

export function useLocale() {
  return useContext(LocaleContext);
}

export function useT() {
  const { locale } = useContext(LocaleContext);
  return (key: TKey | string) => translate(locale, key);
}

const LOCALE_KEY = "agency.locale";
export function loadLocale(fallback: Locale): Locale {
  try {
    const v = localStorage.getItem(LOCALE_KEY);
    return v === "en" || v === "es" ? v : fallback;
  } catch {
    return fallback;
  }
}
/** Fired on window whenever the stored locale changes, so providers mounted above LocaleContext (Clerk) can follow it. */
export const LOCALE_EVENT = "agency:locale";

export function storeLocale(l: Locale) {
  try {
    localStorage.setItem(LOCALE_KEY, l);
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new Event(LOCALE_EVENT));
  } catch {
    /* ignore */
  }
}
